import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import { AddLink, ArticleOutlined, Close, CodeOutlined } from '@mui/icons-material';
import { useEffect, useState } from 'react';

interface AddUrlSourceModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (url: string, cleanMode: boolean) => Promise<void>;
}

type Mode = 'clean' | 'html';

const MODE_CARDS: Array<{
  value: Mode;
  title: string;
  hint: string;
  Icon: typeof ArticleOutlined;
  recommended?: boolean;
}> = [
  {
    value: 'clean',
    title: 'Только текст',
    hint: 'Чистый текст без HTML — оптимально для LLM',
    Icon: ArticleOutlined,
    recommended: true,
  },
  {
    value: 'html',
    title: 'HTML целиком',
    hint: 'Полный снимок страницы со встроенными CSS/JS',
    Icon: CodeOutlined,
  },
];

const AddUrlSourceModal = ({ open, onClose, onSubmit }: AddUrlSourceModalProps) => {
  const [url, setUrl] = useState('');
  const [mode, setMode] = useState<Mode>('clean');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setUrl('');
      setMode('clean');
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    const trimmed = url.trim();
    if (!trimmed || submitting) return;
    try {
      setSubmitting(true);
      await onSubmit(trimmed, mode === 'clean');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3 } } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AddLink />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              Новый URL-источник
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Бэк скачает страницу и сохранит её для контекста чата
            </Typography>
          </Box>
          {!submitting && (
            <IconButton size="small" onClick={onClose} edge="end">
              <Close fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Ссылка на страницу"
          placeholder="https://example.com/page"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          fullWidth
          size="small"
          autoFocus
          disabled={submitting}
          sx={{ mt: 1, mb: 2.5 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && url.trim() && !submitting) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />

        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{ mb: 1, display: 'block' }}
        >
          РЕЖИМ ПАРСИНГА
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.25,
            mb: 1.5,
          }}
        >
          {MODE_CARDS.map(({ value, title, hint, Icon, recommended }) => {
            const selected = mode === value;
            return (
              <Paper
                key={value}
                variant="outlined"
                onClick={() => !submitting && setMode(value)}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  borderWidth: 2,
                  borderColor: selected ? 'primary.main' : 'divider',
                  bgcolor: selected
                    ? (theme) => alpha(theme.palette.primary.main, 0.06)
                    : 'background.paper',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                  opacity: submitting ? 0.6 : 1,
                  '&:hover': submitting
                    ? {}
                    : {
                        borderColor: 'primary.main',
                      },
                }}
              >
                {recommended && (
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 8,
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      color: 'primary.main',
                      letterSpacing: '0.04em',
                    }}
                  >
                    ★
                  </Typography>
                )}
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <Icon
                    sx={{
                      color: selected ? 'primary.main' : 'text.secondary',
                      fontSize: 22,
                      mt: 0.25,
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2, mb: 0.25 }}>
                      {title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                      {hint}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Добавление может занять до 30 секунд — бэк скачивает страницу синхронно.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!url.trim() || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: 'none', borderRadius: 2, px: 2.5 }}
        >
          {submitting ? 'Скачивание...' : 'Добавить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUrlSourceModal;
